import React, { useState } from 'react';
import { useAuth } from '@/context/auth';
import { hasSuperAdminAccess } from '@/context/auth/types';
import { useRestaurantImages, useRestaurantVideos } from '@/hooks/useRestaurantMedia';
import { MediaCard } from '../../MediaCard';
import { MediaFilters } from '../../MediaFilters';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshCw, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

export const LibraryTab: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user ? hasSuperAdminAccess(user.role) : false;
  const [activeTab, setActiveTab] = useState<'images' | 'videos'>('images');
  const [filters, setFilters] = useState({
    status: undefined as string | undefined,
    source_type: undefined as string | undefined
  });

  // Fetch both images and videos
  const {
    images,
    isLoading: imagesLoading,
    error: imagesError,
    refetch: refetchImages,
    deleteImage,
    isDeleting: isDeletingImage
  } = useRestaurantImages(user!.organisationId, filters);

  const {
    videos,
    isLoading: videosLoading,
    error: videosError,
    refetch: refetchVideos,
    deleteVideo,
    isDeleting: isDeletingVideo
  } = useRestaurantVideos(user!.organisationId, filters);

  return (
    <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'images' | 'videos')} className="w-full">
      <TabsList className={`grid w-full ${isSuperAdmin ? 'grid-cols-2' : 'grid-cols-1'} mb-4`}>
        <TabsTrigger value="images" className="flex items-center gap-2">
          <ImageIcon className="h-4 w-4 stroke-white" />
          Images
        </TabsTrigger>
        {isSuperAdmin && (
          <TabsTrigger value="videos" className="flex items-center gap-2">
            <VideoIcon className="h-4 w-4 stroke-white" />
            Videos
          </TabsTrigger>
        )}
      </TabsList>

      {/* Images Tab */}
      <TabsContent value="images" className="space-y-4">
        {imagesError ? (
          <Alert variant="destructive">
            <AlertDescription>
              Failed to load images: {(imagesError as Error).message}
              <Button variant="outline" size="sm" onClick={() => refetchImages()} className="ml-2">
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="flex justify-between items-center">
              <MediaFilters
                filters={filters}
                onChange={setFilters}
                showSourceFilter={true}
              />
              <Button variant="outline" size="sm" onClick={() => refetchImages()} disabled={imagesLoading}>
                <RefreshCw className={cn("h-4 w-4 mr-2", imagesLoading && "animate-spin")} />
                Refresh
              </Button>
            </div>

            {imagesLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="aspect-square bg-muted animate-pulse rounded" />
                ))}
              </div>
            ) : images.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No images found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload or generate your first image to get started
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map((item) => (
                  <MediaCard
                    key={item.id}
                    media={item}
                    onDelete={deleteImage}
                    showActions
                  />
                ))}
              </div>
            )}
          </>
        )}
      </TabsContent>

      {/* Videos Tab - Only for Super Admins */}
      {isSuperAdmin && (
        <TabsContent value="videos" className="space-y-4">
          {videosError ? (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load videos: {(videosError as Error).message}
                <Button variant="outline" size="sm" onClick={() => refetchVideos()} className="ml-2">
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <MediaFilters filters={filters} onChange={setFilters} />
                <Button variant="outline" size="sm" onClick={() => refetchVideos()} disabled={videosLoading}>
                  <RefreshCw className={cn("h-4 w-4 mr-2", videosLoading && "animate-spin")} />
                  Refresh
                </Button>
              </div>

              {videosLoading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="aspect-square bg-muted animate-pulse rounded" />
                  ))}
                </div>
              ) : videos.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No videos found</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Generate your first video to get started
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {videos.map((item) => (
                    <MediaCard
                      key={item.id}
                      media={item}
                      onDelete={deleteVideo}
                      showActions
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </TabsContent>
      )}
    </Tabs>
  );
};
